import React, { useState, useEffect } from "react";

// Modules
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

// App
import { postNode } from "../../core/postNode";
import PageHeader from "../../partials/pageHeader";
import AlertModal from "../../partials/alertModal";
import { getEntityCategories } from "./postHelpers";
import NestedCategory from "../nestedCategory";
import { alertMessages } from "../alertMessages";
import Error from "../error";

// UI components
import { Container, Row, Col } from "react-bootstrap";
import {
  Box,
  TextField,
  CircularProgress,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import CompanyappLoader from "../companyappLoader";

export default function PostAdd(props) {
  const user = useSelector((state) => state).authReducer.user;

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState({
    value: 0,
    label: "Select a category",
  });

  const [alertType, setAlertType] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertBody, setAlertBody] = useState("");
  const [alertConfirmButton, setAlertConfirmButton] = useState(false);

  const [btnLoading, setBtnLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    getEntityCategories({
      user,
      setIsLoading,
      setCategories,
      _setError,
      targetVid: props.targetVid,
    });
    // eslint-disable-next-line
  }, []);

  /**
   * @function setError
   * @description Updates the state of the component upon an error
   * @param {boolean} _isError
   * @param {int} _errorStatus
   * @param {string} _errorMessage
   */
  // eslint-disable-next-line
  const _setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);
    setBtnLoading(false);
    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  const addPost = () => {
    setIsLoading(true);
    setBtnLoading(true);
    const _extraBlocks = props.extraBlocks ? props.extraBlocks : [];
    const _blocks = props.onlySummary
      ? [
          {
            data: {
              text: summary,
            },
            type: "summary",
          },
        ]
      : [
          {
            data: {
              text: summary,
            },
            type: "summary",
          },
          {
            data: {
              text: "",
            },
            type: "body",
          },
          ..._extraBlocks,
        ];
    const blankEditorObj = {
      time: Date.now(),
      blocks: [..._blocks],
    };

    const data = {
      title: title,
      body: blankEditorObj,
      field_category: [selectedCategoryItem],
    };

    postNode(props.addPostEndpoint, data, user.csrf_token)
      .then((response) => {
        if (response.status === 201 && response.data && response.data.id) {
          navigate(`${props.editRoute}/${response.data.id}`);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setBtnLoading(false);
        setModalVisible(true);
        setAlertType("error");
        setAlertTitle(alertMessages.postNodeFailed.title);
        setAlertBody(alertMessages.postNodeFailed.message);
        setAlertConfirmButton(true);
      });
  };

  const errorAlerts = () => {
    return (
      <AlertModal
        showAlert={modalVisible}
        showAlertCallback={() => setModalVisible(false)}
        alertType={alertType}
        alertMessageTitle={alertTitle}
        alertMessageBody={alertBody}
        confirmButton={alertConfirmButton}
      />
    );
  };

  return (
    <>
      {isError ? (
        <Error status={errorStatus} message={errorMessage} />
      ) : (
        <main className={`add-${props.entityType} screen add-screen`}>
          <PageHeader pageName={props.pageTitle} filters={false} />

          {errorAlerts()}
          <div className="add-content">
            <Container>
              {isLoading ? (
                <CompanyappLoader />
              ) : (
                <Row>
                  <Col xs={12}>
                    <Box className="ls">
                      <div className="form-group mb-3">
                        <Typography
                          className="mb-1"
                          owner="admin"
                          variant="body2"
                          color="adminPrimary.main"
                          weight="medium"
                          lh="medium"
                        >
                          Title*
                        </Typography>
                        <TextField
                          required
                          id="post-title"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={title}
                          sx={{
                            "& .MuiInputBase-input": {
                              height: 28,
                              // paddingTop: '11px',
                              // paddingBottom: '11px'
                            },
                          }}
                          onInput={(event) => {
                            setTitle(event.target.value);
                          }}
                        />
                      </div>
                      <div className="form-group mb-3">
                        <Typography
                          className="mb-1"
                          owner="admin"
                          variant="body2"
                          color="adminPrimary.main"
                          weight="medium"
                          lh="medium"
                        >
                          Summary
                        </Typography>
                        <TextField
                          required
                          id="post-summary"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={summary}
                          sx={{
                            "& .MuiInputBase-input": {
                              height: 28,
                              // paddingTop: '11px',
                              // paddingBottom: '11px'
                            },
                          }}
                          onInput={(event) => {
                            setSummary(event.target.value);
                          }}
                        />
                      </div>
                      <div className="form-group mb-3">
                        <NestedCategory
                          categories={categories}
                          selectedCategoryItem={
                            selectedCategoryItem?.value === 0
                              ? { label: selectedCategoryItem?.label, value: 0 }
                              : selectedCategoryItem
                          }
                          title={
                            <Typography
                              className="mb-1"
                              owner="admin"
                              variant="body2"
                              color="adminPrimary.main"
                              weight="medium"
                              lh="medium"
                            >
                              Category*
                            </Typography>
                          }
                          categoryName={props.categoryName}
                          hideZero={true}
                          handleCategorySelection={(value) => {
                            setSelectedCategoryItem(value);
                          }}
                        />
                        {/* <CategorySelector
                    name="postCategory"
                    label="Category"
                    categories={categories}
                    selectedCategory={
                      selectedCategoryItem?.value === 0
                        ? { label: selectedCategoryItem?.label, value: 0 }
                        : selectedCategoryItem
                    }
                    outlined={true}
                    variant="outlined"
                    size="small"
                    handleCategorySelection={(tid) => {
                      handleCategorySelection(tid);
                    }}
                  /> */}
                      </div>
                      <div className="form-group">
                        <Stack
                          direction="row"
                          justifyContent="flex-end"
                          alignItems="center"
                          spacing={1}
                        >
                          <Button
                            variant="outlined"
                            sx={{
                              backgroundColor: "primary.contrastText",
                              textTransform: "none",
                              marginRight: "10px",
                            }}
                            className="cancel"
                            onClick={() => {
                              navigate(`${props.returnRoute}`);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            disabled={
                              btnLoading ||
                              title === "" ||
                              selectedCategoryItem.value === 0
                                ? true
                                : false
                            }
                            variant="contained"
                            color="secondary"
                            className="action"
                            sx={{
                              textTransform: "none",
                            }}
                            onClick={() => {
                              addPost();
                            }}
                          >
                            {btnLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              `Create Post`
                            )}
                          </Button>
                        </Stack>
                      </div>
                    </Box>
                  </Col>
                </Row>
              )}
            </Container>
          </div>
        </main>
      )}
    </>
  );
}
